import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import {
  getCostTransactionCategoryKey,
  getCostCategoryListKey,
  getCostKey,
  getCostTransactionByIdKey,
  getCostTransactionGroupKey,
  getCostTransactionLineChartKey,
  getCostTransactionPieChartKey,
  getCostTransactionsHistoryInfiniteKey,
  getCostTransactionsHistoryKey,
  getCostTransactionStackChartKey,
  UNIT,
} from 'common/constants/queryKeys'
import useCycleApi from 'common/hooks/useCycleApi'
import { BasePaginationResponseV2, BaseResponse } from 'common/types/api'
import { formatDate } from 'common/utils/date'
import { selectedCycleIdState } from 'modules/pond/stores/atom'
import { useRecoilValue } from 'recoil'

import { COST_REQUEST_CONFIG, COST_TRANSACTION, getUnits } from '.'
import { selectedCostTransactionHistoryDateState } from '../stores/atom'
import {
  Category,
  Cost,
  CostCategory,
  CostTransactionByID,
  CostTransactionGroup,
  CostTransactionLineChartResponse,
  CostTransactionPieChartResponse,
  CostTransactionResponse,
  CostTransactionStackChartResponse,
} from './types'
import { useIsCycleBeforeThreshold } from 'modules/pond/stores/selector'

const ROWS_PER_PAGE = 10

export const useGetCost = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getCostKey.details(cycleId),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<Cost>>(COST_REQUEST_CONFIG.get)
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
    refetchOnMount: 'always',
  })
}

export const useGetUnits = () => {
  return useQuery({
    queryKey: UNIT,
    queryFn: async () => {
      const {
        data: { data },
      } = await getUnits()

      return data
    },
    staleTime: Infinity,
  })
}

export const useGetCostTransactionsHistory = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { startDate, endDate } = useRecoilValue(selectedCostTransactionHistoryDateState)
  const startISO = startDate
    ? formatDate(typeof startDate === 'string' ? new Date(startDate) : startDate)
    : undefined
  const endISO = endDate
    ? formatDate(typeof endDate === 'string' ? new Date(endDate) : endDate)
    : undefined
  return useQuery({
    queryKey: getCostTransactionsHistoryKey.list(cycleId || '', [startISO ?? '', endISO ?? '']),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<CostTransactionResponse>>({
        ...COST_TRANSACTION.getList,
        params: {
          start_date: startISO,
          end_date: endISO,
        },
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: Boolean(cycleId && startISO && endISO),
  })
}

export const useGetCostTransactionsHistoryInfinite = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { startDate, endDate } = useRecoilValue(selectedCostTransactionHistoryDateState)
  const startISO = startDate
    ? formatDate(typeof startDate === 'string' ? new Date(startDate) : startDate)
    : undefined
  const endISO = endDate
    ? formatDate(typeof endDate === 'string' ? new Date(endDate) : endDate)
    : undefined

  const initParams = {
    params: {
      pageSize: ROWS_PER_PAGE,
      page: 1,
      startDate: startISO,
      endDate: endISO,
    },
    cycleId,
  }

  return useInfiniteQuery({
    queryKey: getCostTransactionsHistoryInfiniteKey.list({
      cycleId: cycleId || '',
      startDate: startISO,
      endDate: endISO,
    }),
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await cycleApi.request<BasePaginationResponseV2<CostTransactionResponse>>({
        ...COST_TRANSACTION.getList,
        ...pageParam,
      })
      return {
        costs: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextPage) {
        return {
          cycleId: initParams.cycleId,
          params: { ...initParams.params, page: lastPage.nextPage },
        }
      }
      return undefined
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetTransactionPieChart = (category_id = '') => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  return useQuery({
    queryKey: getCostTransactionPieChartKey.details(cycleId || '', category_id),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<CostTransactionPieChartResponse>>({
        ...COST_TRANSACTION.getPieChart,
        params: {
          category_id,
        },
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetTransactionStackChart = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { startDate, endDate } = useRecoilValue(selectedCostTransactionHistoryDateState)
  const startISO = startDate ? formatDate(startDate) : undefined
  const endISO = endDate ? formatDate(endDate) : undefined
  const isCycleBeforeThreshold = useIsCycleBeforeThreshold();
  
  return useQuery({
    queryKey: getCostTransactionStackChartKey.details(cycleId ?? '', [startISO ?? '', endISO ?? '']),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<CostTransactionStackChartResponse[]>>({
        ...COST_TRANSACTION.getStackChart,
        params: {
          start_date: startISO,
          end_date: endISO,
        },
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId && !isCycleBeforeThreshold,
  })
}

export const useGetTransactionCategoryList = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  return useQuery({
    queryKey: getCostTransactionCategoryKey.list(cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<CostCategory[]>>({
        ...COST_TRANSACTION.getTransactionCategoryList,
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useCostCategoriesList = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  return useQuery({
    queryKey: getCostCategoryListKey.list(cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<Category[]>>({
        ...COST_TRANSACTION.categoryList,
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetCostTransactionById = (transactionId: string) => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  return useQuery({
    queryKey: getCostTransactionByIdKey.detail(cycleId || '', transactionId),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<CostTransactionByID>>({
        ...COST_TRANSACTION.getById,
        url: `${COST_TRANSACTION.getById.url}/${transactionId}`,
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId && !!transactionId,
  })
}

export const useGetTransactionLineChart = ({
  startDate,
  endDate,
  costPerUnit,
}: {
  startDate?: string
  endDate?: string
  costPerUnit?: 'kg' | 'count'
}) => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getCostTransactionLineChartKey.list(cycleId || '', {
      startDate,
      endDate,
      costPerUnit,
    }),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<CostTransactionLineChartResponse>>({
        ...COST_TRANSACTION.getTransactionLinechart,
        params: {
          startDate,
          endDate,
          costPerUnit,
        },
      })

      return data.data
    },
    enabled: !!cycleId,
    staleTime: Infinity,
  })
}

export const useGetTransactionGroup = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  return useQuery({
    queryKey: getCostTransactionGroupKey.list(cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<CostTransactionGroup[]>>({
        ...COST_TRANSACTION.getGroup,
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}
